import {AuthenticatedUser} from '@oegbv/ui-shared/dist/auth/authenticators/Authenticator'

export interface DigitalUser {
  username?: string
  email?: string
  arfbr?: 'FREE' | 'PLUS' | 'PREMIUM'
  admin?: boolean
  kv?: boolean
  kvSystem?: boolean
  gems?: boolean
  xmlApi?: boolean
  books?: boolean
  lohndatenApi?: boolean
}

export enum Permission {
  API_ADMIN = 'api-admin',
  API_BOOKS = 'api-books',
  API_KVSYSTEM = 'api-kvsystem',
  API_GEMS = 'api-gems',
  API_DIS = 'api-dis',
  API_LOHNDATEN = 'api-lohndaten',
}

const USER_COOKIE_NAME = 'USER'
const SESSION_COOKIE_NAME = 'SESSION'

const LOGIN_PATH = '/api/v1/auth/login'
const LOGOUT_PATH = '/api/v1/auth/logout'
const CODE_PATH = '/api/v1/auth/code'

const SESSION_RECHECK_TIME_IN_MIN = 5

const getCookieValue = (name: string) => {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() ?? null
  } else {
    return null
  }
}

const removeAuthCookies = () => {
  document.cookie = `${SESSION_COOKIE_NAME}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`
  document.cookie = `${USER_COOKIE_NAME}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`
}

function getBrowserBaseUrl() {
  if (typeof window !== 'undefined') {
    const {protocol, hostname, port} = window.location
    const portPart =
      port && ((protocol === 'http:' && port !== '80') || (protocol === 'https:' && port !== '443')) ? `:${port}` : ''
    return `${protocol}//${hostname}${portPart}`
  }
  return ''
}

export class SessionPlusAuthenticator {
  private user: DigitalUser | null = null
  private lastSessionCheck: number | null = null

  init(done: () => void) {
    const userCookieValue = getCookieValue(USER_COOKIE_NAME)
    // Reset auth state if the userCookie, that comes with the /code resources, is not set
    // this means that the auth process wasn't successful before. By removing any state, we prevent
    // sending potential invalid auth information to the server.
    if (userCookieValue === null) {
      removeAuthCookies()
      this.login()
      return
    }
    this.fetchUser().then((user) => {
      if (user !== null) {
        this.startRegularSessionCheck()
      } else {
        removeAuthCookies()
      }
      this.user = user
      done()
    })
  }

  checkSession() {
    this.fetchUser().then((user) => {
      if (user === null) {
        this.logout()
      }
    })
  }

  startRegularSessionCheck() {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        this.checkSession()
      }
    }

    window.addEventListener('focus', () => this.checkSession())
    document.addEventListener('visibilitychange', handleVisibilityChange)

    setInterval(() => {
      this.checkSession()
    }, 1000 * 60 * SESSION_RECHECK_TIME_IN_MIN)
  }

  isLastSessionCheckValid() {
    if (this.lastSessionCheck === null) {
      return false
    }

    const now = new Date().getTime()
    const diff = now - this.lastSessionCheck
    return diff < (SESSION_RECHECK_TIME_IN_MIN - 1) * 60 * 1000
  }

  async fetchUser(): Promise<DigitalUser | null> {
    if (this.user !== null && this.isLastSessionCheckValid()) {
      return this.user
    }

    try {
      const response = await fetch('/api/v1/user')

      if (!response.ok || response.status >= 300) {
        console.error('authenticateSession: Error fetching user', response)
        return null
      }

      this.lastSessionCheck = new Date().getTime()
      return await response.json()
    } catch {
      return null
    }
  }

  hasPermission(permissions: Permission | Permission[]): boolean {
    if (Array.isArray(permissions)) {
      return permissions.some((permission) => this.validatePermission(permission))
    }
    return this.validatePermission(permissions)
  }

  private validatePermission(permission: Permission): boolean {
    if (this.user === null) {
      return false
    }

    if (permission === Permission.API_ADMIN) {
      return this.user.admin === true
    }
    if (permission === Permission.API_BOOKS) {
      return this.user.books === true
    }
    if (permission === Permission.API_KVSYSTEM) {
      return this.user.kvSystem === true
    }
    if (permission === Permission.API_GEMS) {
      return this.user.gems === true
    }
    if (permission === Permission.API_DIS) {
      return this.user.xmlApi === true
    }
    if (permission === Permission.API_LOHNDATEN) {
      return this.user.lohndatenApi === true
    }

    return false
  }

  isAuthenticated(): boolean {
    return this.user !== null
  }

  login(broker?: string | null, redirectUrl?: string | null): void {
    const codeCallbackUrl = `codeCallbackUrl=${encodeURIComponent(getBrowserBaseUrl() + CODE_PATH)}`
    const defaultRedirectUrl = `&redirectUrl=${encodeURIComponent(getBrowserBaseUrl())}`
    if (!broker && !redirectUrl) {
      window.location.href = `${LOGIN_PATH}?${codeCallbackUrl}${defaultRedirectUrl}`
      return
    }

    const encodedRedirectUrl = redirectUrl ? encodeURIComponent(redirectUrl) : undefined
    const encodedBroker = broker ? encodeURIComponent(broker) : undefined

    let nextUrl = `${LOGIN_PATH}?${codeCallbackUrl}`
    if (encodedRedirectUrl !== undefined) {
      nextUrl += `&redirectUrl=${encodedRedirectUrl}`
    } else {
      nextUrl += defaultRedirectUrl
    }
    if (encodedBroker !== undefined) {
      nextUrl += `&broker=${encodedBroker}`
    }
    window.location.href = nextUrl
  }

  logout(redirectUrl?: string | null): void {
    this.user = null
    this.lastSessionCheck = null

    if (!redirectUrl) {
      window.location.href = `${LOGOUT_PATH}?redirectUrl=${encodeURIComponent(getBrowserBaseUrl())}`
      return
    }

    const encodedRedirectUrl = encodeURIComponent(redirectUrl)
    window.location.href = `${LOGOUT_PATH}?redirectUrl=${encodedRedirectUrl}`
  }

  currentUser(): AuthenticatedUser {
    return {
      name: this.user?.username || 'unknown',
      email: this.user?.email || 'unknown',
      id: '',
    }
  }
}

export const authenticator = new SessionPlusAuthenticator()
